import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Dialog from 'apollo-react/components/Dialog';
import DialogActions from 'apollo-react/components/DialogActions';
import DialogContent from 'apollo-react/components/DialogContent';
import DialogTitle from 'apollo-react/components/DialogTitle';
import IconButton from 'apollo-react/components/IconButton';
import Paper from 'apollo-react/components/Paper';
import Typography from 'apollo-react/components/Typography';
import Close from 'apollo-react-icons/Close';
import React, { useCallback, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { connect, useSelector } from 'react-redux';
import { ResizableBox } from 'react-resizable';

import { clearDocumentViewer } from '../../actions/DocumentViewerActions';
import { setAttachmentPreview } from '../../actions/EntityDisplayActions';
import FileViewer from '../../components/FileViewer';
import { CERTIFICATION_PAGE } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getCurrentAttachmentText } from '../../selectors/AttachmentsSelectors';
import { getPagePathname } from '../../utils/childrenWindowsUtils';
import { cleanTextFromHTML, getUITA } from '../../utils/commonUtils';

const MAX_PAPER_WIDTH = 1200;
const DIALOG_CONTENT_PADDING_VERTICAL = 8;

const useStyles = makeStyles({
  modal: {
    width: '100%',
    maxWidth: '100%',
  },
  paper: {
    outline: 'none',
    maxWidth: MAX_PAPER_WIDTH,
    width: '100%',
    height: '90vh',
    '@media (max-width: 480px)': {
      margin: '16px',
      height: '95vh',
    },
  },
  resizable: {
    position: 'relative',
    '& .react-resizable-handle': {
      position: 'absolute',
      width: 20,
      height: 20,
      bottom: 0,
      right: 0,
      background:
        "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+')",
      'background-position': 'bottom right',
      padding: '0 3px 3px 0',
      'background-repeat': 'no-repeat',
      'background-origin': 'content-box',
      'box-sizing': 'border-box',
      cursor: 'se-resize',
    },
  },
  contentRoot: {
    paddingTop: '0 !important',
    overflow: 'hidden',
    '@media (max-width: 480px)': {
      padding: '8px 12px',
    },
  },
  closeButton: {
    position: 'absolute',
    right: 20,
    top: 20,
    color: '#999',
    '@media (max-width: 480px)': {
      right: 10,
      top: 10,
    },
  },
});

const AttachmentPreviewDialog = ({ attachment, setAttachmentPreview, clearDocumentViewer }) => {
  const classes = useStyles();

  const [open, setOpen] = useState();
  const [height, setHeight] = useState();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dialogRef = useCallback((node) => {
    if (node !== null) {
      setHeight(node.getBoundingClientRect().height - DIALOG_CONTENT_PADDING_VERTICAL);
    }
  });

  useEffect(() => {
    setOpen(!!attachment);
  }, [attachment]);

  const handleClose = () => {
    const pathname = getPagePathname();

    setAttachmentPreview({ isOpen: false });
    pathname !== CERTIFICATION_PAGE && clearDocumentViewer();
    setOpen(false);
  };

  return (
    <div>
      {!!attachment && (
        <Dialog
          open={open}
          maxWidth={false}
          disableBackdropClick
          className={classes.modal}
          classes={{ paper: classes.paper }}
          PaperComponent={(paperProps) => (
            <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiButtonBase-root"]'}>
              <Paper {...paperProps} />
            </Draggable>
          )}
          aria-labelledby="draggable-dialog-title"
        >
          <CustomDialogTitle
            style={{ cursor: 'move' }}
            id="draggable-dialog-title"
            onClose={handleClose}
          >
            {attachment.filename}&nbsp;
          </CustomDialogTitle>
          <DialogContent classes={{ root: classes.contentRoot }} ref={dialogRef}>
            {height ? (
              <ResizableBox height={height} width="100%" className={classes.resizable}>
                <FileViewer
                  filename={attachment.filename}
                  params={attachment.params}
                  height={height}
                  isAttachment
                />
              </ResizableBox>
            ) : null}
          </DialogContent>
          <DialogButtons
            attachmentId={attachment.params?.ItmId || attachment.params?.TempAttachmentID}
            handleClose={handleClose}
          />
        </Dialog>
      )}
    </div>
  );
};

const CustomDialogTitle = (props) => {
  const classes = useStyles();

  const { children, onClose, ...other } = props;
  return (
    <DialogTitle disableTypography {...other}>
      <Typography component="h3" variant="h6" ui-ta={getUITA('dialog-title')}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          ui-ta={getUITA('dialog-close-button')}
        >
          <Close />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

const DialogButtons = ({ attachmentId, handleClose }) => {
  const translateString = useTranslation();
  const attachmentText = useSelector((state) => getCurrentAttachmentText(state, { attachmentId }));

  const handleCopyAndClose = () => {
    navigator.clipboard.writeText(cleanTextFromHTML(attachmentText));
    handleClose();
  };

  return (
    <DialogActions>
      {attachmentText && (
        <Button variant="primary" onClick={handleCopyAndClose}>
          {translateString('Copy and Close')}
        </Button>
      )}
    </DialogActions>
  );
};

const mapStateToProps = (state) => ({
  attachment: state.entityDisplayPage.attachment,
});

const mapDispatchToProps = {
  setAttachmentPreview,
  clearDocumentViewer,
};

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentPreviewDialog);
